import type {
  AfterViewInit,
  EmbeddedViewRef,
  TemplateRef,
} from '@angular/core';
import {
  DestroyRef,
  Directive,
  ElementRef,
  Injector,
  Renderer2,
  ViewContainerRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { isEqual } from 'lodash';
import {
  combineLatestWith,
  distinctUntilChanged,
  fromEventPattern,
} from 'rxjs';

interface OverflowItemContext<T> {
  item: T;
  index: number;
}

interface OverflowCounterContext<T> {
  count: number;
  hiddenItems: T[];
}

@Directive({
  selector: '[onyxOverflowItems]',
})
export class OnyxOverflowDirective<T> implements AfterViewInit {
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly injector = inject(Injector);
  private readonly renderer = inject(Renderer2);
  private readonly destroyRef = inject(DestroyRef);

  public readonly onyxOverflowItems = input.required<T[]>();
  public readonly onyxOverflowItemTemplateRef =
    input<TemplateRef<OverflowItemContext<T>>>();
  public readonly onyxOverflowCounterTemplateRef =
    input<TemplateRef<OverflowCounterContext<T>>>();
  public readonly onyxOverflowSeparator = input<string>();
  public readonly onyxOverflowShowCounter = input(true);
  public readonly onyxOverflowHiddenItemsChange = output<T[] | null>();

  private readonly hostElement = this.elementRef.nativeElement;

  private views = signal<EmbeddedViewRef<unknown>[]>([]);

  public ngAfterViewInit(): void {
    const items$ = toObservable(this.onyxOverflowItems, {
      injector: this.injector,
    }).pipe(
      distinctUntilChanged((previous, current) => isEqual(previous, current)),
    );

    fromEventPattern<void>(
      (handler) => {
        const observer = new ResizeObserver(() =>
          requestAnimationFrame(() => handler()),
        );
        observer.observe(this.hostElement);
        return observer;
      },
      (_handler, observer?: ResizeObserver) => observer?.disconnect(),
    )
      .pipe(combineLatestWith(items$), takeUntilDestroyed(this.destroyRef))
      .subscribe(([, items]) => this.render(items));
  }

  private render(items: T[]): void {
    this.clear();

    let lastRenderedIndex = this.createItems(items);

    if (lastRenderedIndex === items.length - 1) {
      this.onyxOverflowHiddenItemsChange.emit(null);
      return;
    }

    if (lastRenderedIndex < 0) {
      this.clear();
      this.createCounter(items);
      return;
    }

    while (true) {
      this.createCounter(items.slice(lastRenderedIndex + 1));

      if (lastRenderedIndex === -1) break;
      if (!this.hasOverflow()) break;

      --lastRenderedIndex;
      this.deleteLastChildren(this.onyxOverflowSeparator() != null ? 3 : 2);
    }
  }

  private createItems(items: T[]): number {
    let lastRenderedIndex = -1;
    for (const [index, item] of items.entries()) {
      let separator = false;
      if (index > 0) separator = this.createSeparator();
      this.createItem(item, index);

      if (this.hasOverflow()) {
        this.deleteLastChildren(separator ? 2 : 1);
        return index - 1;
      }

      lastRenderedIndex = index;
    }

    return lastRenderedIndex;
  }

  private createItem(item: T, index: number): void {
    const template = this.onyxOverflowItemTemplateRef();
    if (template) {
      const view = this.viewContainerRef.createEmbeddedView<
        OverflowItemContext<T>
      >(template, { item, index }, { injector: this.injector });
      this.views.update((views) => [...views, view]);
      view.detectChanges();

      for (const node of view.rootNodes) {
        this.renderer.appendChild(this.hostElement, node);
      }

      return;
    }

    const span = this.renderer.createElement('span');
    const text = this.renderer.createText(String(item));
    this.renderer.appendChild(span, text);
    this.renderer.appendChild(this.hostElement, span);
  }

  private createSeparator(): boolean {
    const separator = this.onyxOverflowSeparator();
    if (!separator) return false;

    const span = this.renderer.createElement('span');
    const text = this.renderer.createText(separator.replace(' ', '\u00A0'));
    this.renderer.appendChild(span, text);
    this.renderer.appendChild(this.hostElement, span);

    return true;
  }

  private createCounter(hiddenItems: T[]): void {
    if (!this.onyxOverflowShowCounter()) return;

    const template = this.onyxOverflowCounterTemplateRef();
    if (template) {
      const view = this.viewContainerRef.createEmbeddedView<
        OverflowCounterContext<T>
      >(
        template,
        { count: hiddenItems.length, hiddenItems },
        { injector: this.injector },
      );
      this.views.update((views) => [...views, view]);
      view.detectChanges();

      for (const node of view.rootNodes) {
        this.renderer.appendChild(this.hostElement, node);
      }
    } else {
      const span = this.renderer.createElement('span');
      const text = this.renderer.createText(`\u00A0+${hiddenItems.length}`);
      this.renderer.appendChild(span, text);
      this.renderer.appendChild(this.hostElement, span);
    }

    this.onyxOverflowHiddenItemsChange.emit(
      hiddenItems?.length ? hiddenItems : null,
    );
  }

  private clear(): void {
    for (const view of this.views()) view.destroy();
    this.deleteLastChildren(this.hostElement.children.length);

    this.views.set([]);
    this.viewContainerRef.clear();
  }

  private deleteLastChildren(count = 1): void {
    for (let i = 0; i < count; ++i) {
      this.renderer.removeChild(this.hostElement, this.hostElement.lastChild!);
    }
  }

  private hasOverflow(): boolean {
    return this.hostElement.scrollWidth > this.hostElement.clientWidth;
  }
}
